import ToolReceipt from './ToolReceipt.js?v=1.0.18';
import { DOCUMENT_PREVIEW_STAGES, readablePreviewText, previewMarkdown, previewChanges, reconcilePreviewBlocks, previewComparisonSections, previewDocumentSections, advancePreviewHighlights, mapPreviewTextOffset } from './documentPreviewRendering.js';

export default {
  props: { preview: { type: Object, required: true }, chatUuid: { type: String, required: true }, receipt: { type: Object, default: null } },
  inject: { setProgrammaticChatScrollTop: { default: null }, isProgrammaticChatScroll: { default: null }, isChatViewportLayoutScroll: { default: null }, stopDocumentPreviewFollowing: { default: null }, preserveDocumentPreviewPosition: { default: null }, canFollowDocumentPreview: { default: null }, claimDocumentPreviewReveal: { default: null }, retryDocumentPreview: { default: null } },
  data() {
    return { view: 'document', blocks: [], nextBlock: 0, projection: null, latchedCommitted: null, versions: [], selectedVersion: null,
      controller: null, fetchEpoch: 0, retryTimer: null, retries: 0, access: '', feedback: '', actionPending: false, following: true,
      lastChange: '', highlightState: null, comparisonCache: null, programmatic: false, scrollHost: null, contentCache: new Map(), revealDone: false,
      comparisonBlocks: [], comparisonSections: [], activeChangeKey: '', observedCandidate: null, pendingUpdates: 0, updateSerial: 0, helpOpen: false,
      revealingChange: false, readerNavigationRevision: 0,
      updateNoticeHidden: false, updateNoticeTimer: null,
      savedComparisonBase: null, pendingComparisonVersion: null, pendingCommittedVersion: null, deniedVersionEpochs: new Map(), comparisonFeedback: '', versionsFeedback: '', retryVersion: null };
  },
  computed: {
    editing() { return this.preview.mode === 'edit'; },
    versionRetained() {
      const counts = this.receipt?.ui_payload?.counts;
      const status = this.receipt?.status;
      return ((['completed', 'cancelled'].includes(status) && counts?.no_changes_needed === 1)
        || (['partial', 'cancelled'].includes(status) && counts?.existing_version_retained === 1))
        && (!this.selectedVersion || Number(this.selectedVersion) === Number(this.receipt?.ui_payload?.counts?.version));
    },
    disclosureRevoked() { return this.access === 'revoked' || this.preview.authorization === 'revoked' || Boolean(this.preview.clear_preview); },
    committed() {
      if (this.latchedCommitted) return this.latchedCommitted;
      if (this.preview.committed) return this.preview.committed;
      const receipt = this.receipt;
      const version = Number(receipt?.ui_payload?.counts?.version);
      if (receipt?.result_ref?.type === 'document' && receipt.result_ref.id
        && (['completed', 'partial'].includes(receipt.status) || this.versionRetained) && Number.isInteger(version) && version > 0) {
        return { artifact_id: String(receipt.result_ref.id), version };
      }
      return null;
    },
    committedKey() { return this.committed ? `${this.chatUuid}:${this.committed.artifact_id}:${this.committed.version}` : ''; },
    ready() { return Boolean(this.projection && this.projection.available !== false && !this.access && !this.disclosureRevoked); },
    title() { return this.disclosureRevoked ? 'Documento' : this.projection?.title || this.preview.title || this.preview.display_metadata?.title || 'Documento'; },
    status() {
      if (this.ready && this.versionRetained) return this.receipt?.ui_payload?.title
        || (this.receipt?.ui_payload?.counts?.existing_version_retained === 1 ? 'Versión actual disponible' : 'No se necesitan cambios');
      if (this.ready) return 'Documento guardado';
      if (this.disclosureRevoked) return 'El documento ya no está disponible';
      if (this.access || this.preview.disclosure_paused) return 'Comprobando el acceso al documento';
      if (this.preview.unavailable) return 'Vista previa no disponible';
      if (this.committed) return 'Guardando';
      if (!this.editing && this.preview.stage === 'editing') return 'Redactando';
      return DOCUMENT_PREVIEW_STAGES[this.preview.stage] || 'Preparando el documento';
    },
    incomplete() { return !this.committed && ['incomplete', 'cancelled', 'failed', 'superseded'].includes(this.preview.stage); },
    hasDocument() { return Boolean(readablePreviewText(this.candidate).trim()); },
    showComparisonViews() { return !this.versionRetained && this.editing && this.hasDocument && (!this.projection || Boolean(this.parentVersion)); },
    candidate() {
      if (this.disclosureRevoked) return '';
      return this.projection ? String(this.projection.content_rendered_markdown || this.projection.content_markdown || '')
        : String(this.preview.content || this.preview.base_content || '');
    },
    cleanExport() { return this.ready ? String(this.projection.content_export_markdown || this.projection.content_markdown || '') : ''; },
    changeSummary() { return this.ready ? String((this.versionRetained ? this.receipt?.ui_payload?.summary : this.projection.change_summary) || '') : ''; },
    savedMetadata() {
      if (!this.ready || !this.selectedVersion) return '';
      const parts = [`Versión ${this.selectedVersion}`];
      const words = this.receipt?.ui_payload?.counts?.words;
      if (Number(this.selectedVersion) === Number(this.committed?.version) && words !== null && typeof words !== 'boolean'
        && Number.isInteger(Number(words)) && Number(words) >= 0) parts.push(`${Number(words)} palabras`);
      return parts.join(' · ');
    },
    warnings() {
      if (!this.ready || Number(this.selectedVersion) !== Number(this.committed?.version)) return [];
      const values = this.receipt?.ui_payload?.warnings;
      return Array.isArray(values) ? values.filter(value => typeof value === 'string').slice(0, 20) : [];
    },
    parentVersion() {
      const version = Number(this.projection?.parent_version);
      return Number.isInteger(version) && version > 0 ? version : null;
    },
    comparing() { return !this.versionRetained && this.editing && this.view === 'changes' && (!this.projection || Boolean(this.parentVersion)); },
    comparisonDenied() {
      const version = this.projection ? this.parentVersion : this.pendingComparisonVersion;
      return Boolean(version && (this.deniedVersionEpochs.get(Number(version)) === this.fetchEpoch
        || this.versions.some(item => Number(item.version_number) === Number(version) && item.available === false)));
    },
    comparisonReady() { return !this.comparisonDenied && (!this.projection || this.savedComparisonBase !== null); },
    comparisonBase() { return this.comparisonDenied ? '' : this.projection ? this.savedComparisonBase || '' : this.preview.base_content || ''; },
    renderKey() { return `${this.disclosureRevoked}:${this.view}:${this.projection?.selected_version || ''}:${this.candidate}:${this.comparing}:${this.comparisonReady}:${this.editing ? this.comparisonBase : ''}`; },
    viewportMetadata() { return `${this.title}:${this.status}:${this.preview.section || ''}:${this.comparisonTitle}:${this.comparisonDescription}:${this.versions.length}:${this.showUpdateNotice}`; },
    changeSections() { return this.versionRetained ? [] : this.comparisonSections.filter(section => section.kind === 'change'); },
    activeChangeIndex() { return Math.max(0, this.changeSections.findIndex(section => section.key === this.activeChangeKey)); },
    changeCounts() {
      return this.changeSections.reduce((counts, section) => ({ additions: counts.additions + section.model.counts.additions,
        removals: counts.removals + section.model.counts.removals }), { additions: 0, removals: 0 });
    },
    comparisonTruncated() { return this.changeSections.some(section => section.model.truncated); },
    comparisonTitle() {
      return this.projection && this.parentVersion ? `De la versión ${this.parentVersion} a la ${this.selectedVersion}` : 'Cambios en el documento';
    },
    comparisonDescription() {
      return this.projection && this.parentVersion ? `Versión ${this.selectedVersion} frente a versión ${this.parentVersion}` : 'Documento original frente a los cambios recibidos';
    },
    comparisonId() { return `preview-comparison-${this.preview.preview_id}`; },
    editComplete() { return this.editing && this.ready && Number(this.selectedVersion) === Number(this.committed?.version); },
    showUpdateNotice() {
      return this.editing && !this.disclosureRevoked && !this.access && (this.editComplete || !this.projection)
        && !(this.editComplete && this.updateNoticeHidden);
    },
    updateAnnouncement() {
      if (this.versionRetained) return 'No se realizaron cambios';
      if (this.editComplete) return 'Edición completada';
      if (this.incomplete) return 'Edición interrumpida · No guardada';
      if (this.pendingUpdates) return this.pendingUpdates === 1 ? '1 actualización nueva' : `${this.pendingUpdates} actualizaciones nuevas`;
      return this.status;
    },
    displaySections() {
      if (this.versionRetained) return previewDocumentSections(this.blocks, []);
      return this.comparing ? this.comparisonSections : previewDocumentSections(this.blocks, this.comparisonSections);
    },
  },
  watch: {
    editComplete: {
      immediate: true,
      handler(complete) {
        clearTimeout(this.updateNoticeTimer);
        this.updateNoticeTimer = null;
        this.updateNoticeHidden = false;
        if (complete) {
          this.updateNoticeTimer = setTimeout(() => {
            this.updateNoticeHidden = true;
            this.updateNoticeTimer = null;
          }, 3000);
        }
      },
    },
    renderKey: { immediate: true, handler() { this.updateBlocks(); } },
    committedKey: { immediate: true, handler(key) { if (key) this.loadCommitted(); } },
    viewportMetadata() {
      this.preserveDocumentPreviewPosition?.();
      const reader = this.captureReader();
      const revision = this.readerNavigationRevision;
      this.$nextTick(() => {
        if (revision === this.readerNavigationRevision) this.restoreReader(reader);
      });
    },
  },
  mounted() {
    this.scrollHost = this.$el.closest('.chat-scroll-area') || this.findScrollHost();
    this.scrollHost?.addEventListener('wheel', this.stopFollowing, { passive: true });
    this.scrollHost?.addEventListener('touchmove', this.stopFollowing, { passive: true });
    this.scrollHost?.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('selectionchange', this.onSelection);
    const inspecting = this.hasReaderSelection();
    if (inspecting) this.stopFollowing();
    const mayReveal = !inspecting && (
      this.claimDocumentPreviewReveal?.(this.preview) === true || this.canFollowDocumentPreview?.() === true
    );
    this.following = mayReveal;
    this.preserveDocumentPreviewPosition?.();
    this.$nextTick(() => {
      if (this.revealDone || !mayReveal || !this.scrollHost || !this.following || this.hasReaderSelection()) return;
      this.revealDone = true; this.programmatic = true;
      const host = this.scrollHost;
      const position = host.scrollTop + this.$el.getBoundingClientRect().top - host.getBoundingClientRect().top;
      if (this.setProgrammaticChatScrollTop) this.setProgrammaticChatScrollTop(host, position);
      else host.scrollTop = position;
      this.preserveDocumentPreviewPosition?.();
      requestAnimationFrame(() => { this.programmatic = false; });
    });
  },
  beforeUnmount() {
    clearTimeout(this.updateNoticeTimer);
    this.fetchEpoch++; this.controller?.abort(); clearTimeout(this.retryTimer);
    this.scrollHost?.removeEventListener('wheel', this.stopFollowing);
    this.scrollHost?.removeEventListener('touchmove', this.stopFollowing);
    this.scrollHost?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('selectionchange', this.onSelection);
  },
  methods: {
    findScrollHost() { let el = this.$el.parentElement; while (el) { if (/auto|scroll/.test(getComputedStyle(el).overflowY)) return el; el = el.parentElement; } return null; },
    readerScrollHost() {
      const document = this.$refs?.documentContent;
      // Watchers capture the current DOM before Vue switches between views.
      return document?.classList.contains('artifact-difference__document') ? document : this.scrollHost;
    },
    stopFollowing() { this.following = false; this.stopDocumentPreviewFollowing?.(); },
    hasReaderSelection() {
      const selection = typeof window !== 'undefined' && window.getSelection?.();
      return Boolean(selection && !selection.isCollapsed && (this.scrollHost || this.$el)?.contains(selection.anchorNode));
    },
    captureReader() {
      if (this.revealingChange || !this.$el || !this.scrollHost) return null;
      const scrollHost = this.readerScrollHost();
      const host = scrollHost.getBoundingClientRect();
      const chat = this.scrollHost.getBoundingClientRect();
      const top = Math.max(host.top, chat.top, 0); const bottom = Math.min(host.bottom, chat.bottom, window.innerHeight);
      const left = Math.max(host.left, chat.left, 0); const right = Math.min(host.right, chat.right, window.innerWidth);
      const selection = window.getSelection(); let selected = null;
      // A later card must not adjust the shared chat while another document is
      // being inspected, even if a small part of this card is also visible.
      if (selection && !selection.isCollapsed && !this.$el.contains(selection.anchorNode)) return null;
      const anchor = top < bottom && left < right ? [...this.$el.querySelectorAll('[data-preview-block]')].find(el => {
        const rect = el.getBoundingClientRect();
        return rect.bottom > top && rect.top < bottom && rect.right > left && rect.left < right;
      }) : null;
      if (selection && !selection.isCollapsed && selection.rangeCount && this.$el.contains(selection.anchorNode) && this.$el.contains(selection.focusNode)) {
        const describe = (node, offset, affinity) => {
          const block = (node.nodeType === 1 ? node : node.parentElement)?.closest('[data-preview-block]');
          const content = block?.querySelector('[data-preview-block-content]');
          if (!content?.contains(node)) return null;
          const range = document.createRange(); range.selectNodeContents(content); range.setEnd(node, offset);
          return { key: block.dataset.previewBlock, offset: range.toString().length, text: content.textContent, affinity };
        };
        const range = selection.getRangeAt(0);
        const forward = range.startContainer === selection.anchorNode && range.startOffset === selection.anchorOffset;
        selected = { anchor: describe(selection.anchorNode, selection.anchorOffset, forward ? 'after' : 'before'),
          focus: describe(selection.focusNode, selection.focusOffset, forward ? 'before' : 'after') };
      }
      return anchor || selected ? { anchor, anchorKey: anchor?.dataset?.previewBlock, anchorText: anchor?.querySelector?.('[data-preview-block-content]')?.textContent,
        offset: anchor?.getBoundingClientRect().top, scrollHost, selected } : null;
    },
    restoreReader(reader) {
      if (!reader || !this.scrollHost) return;
      const exactContent = text => {
        if (!text) return null;
        // A removed paragraph can merge back into an equal candidate. Recover
        // only a unique exact counterpart, never a similar or repeated passage.
        // Markdown paragraphs and diff passages can contribute different
        // trailing block separators while containing the exact same words.
        const matches = [...this.$el.querySelectorAll('[data-preview-block-content]')].filter(el => el.textContent.trimEnd() === text.trimEnd());
        return matches.length === 1 ? matches[0] : null;
      };
      const readingAnchor = (reader.anchorKey && this.$el.querySelector(`[data-preview-block="${reader.anchorKey}"]`)) || (reader.anchor?.isConnected ? reader.anchor
        : exactContent(reader.anchorText)?.closest('[data-preview-block]'));
      const host = reader.scrollHost;
      if (readingAnchor && host === this.readerScrollHost()) {
        const delta = readingAnchor.getBoundingClientRect().top - reader.offset;
        if (Math.abs(delta) > .5) {
          this.programmatic = true;
          const next = host.scrollTop + delta;
          if (host === this.scrollHost && this.setProgrammaticChatScrollTop) this.setProgrammaticChatScrollTop(host, next);
          else host.scrollTop = next;
          this.preserveDocumentPreviewPosition?.();
          requestAnimationFrame(() => { this.programmatic = false; });
        }
      }
      const locate = point => {
        if (!point) return null;
        const content = this.$el.querySelector(`[data-preview-block="${point.key}"] [data-preview-block-content]`) || exactContent(point.text);
        if (!content) return null;
        let offset = mapPreviewTextOffset(point.text, content.textContent, point.offset, point.affinity);
        if (offset === null) return null;
        const walker = document.createTreeWalker(content, NodeFilter.SHOW_TEXT); let node;
        while ((node = walker.nextNode())) { if (offset <= node.textContent.length) return [node, offset]; offset -= node.textContent.length; }
        return null;
      };
      const anchor = locate(reader.selected?.anchor); const focus = locate(reader.selected?.focus);
      if (anchor && focus) window.getSelection()?.setBaseAndExtent(...anchor, ...focus);
    },
    onScroll(event) {
      if (this.programmatic || this.isProgrammaticChatScroll?.(this.scrollHost) || this.isChatViewportLayoutScroll?.(event)) return;
      if (this.comparing) this.trackActiveChange();
      this.stopFollowing();
    },
    onDocumentScroll() {
      if (!this.comparing || this.programmatic) return;
      this.trackActiveChange();
      this.stopFollowing();
    },
    onSelection() {
      if (this.hasReaderSelection()) this.stopFollowing();
    },
    renderMarkdown: ToolReceipt.methods.renderArtifactMarkdown,
    displayArtifactDifferenceSegment: ToolReceipt.methods.displayArtifactDifferenceSegment,
    artifactDifferencePassageHtml: ToolReceipt.methods.artifactDifferencePassageHtml,
    updateBlocks() {
      // Live content and the saved projection retain the reader's position,
      // even if the reader explicitly visited the current document end.
      if (this.scrollHost) this.preserveDocumentPreviewPosition?.();
      if (this.disclosureRevoked) {
        this.blocks = []; this.comparisonCache = null; this.highlightState = null;
        this.comparisonBlocks = []; this.comparisonSections = []; this.pendingUpdates = 0; this.observedCandidate = null;
        this.lastChange = ''; this.contentCache.clear();
        return;
      }
      const reader = this.captureReader();
      const revision = this.readerNavigationRevision;
      const inspecting = this.hasReaderSelection?.();
      if (inspecting) this.stopFollowing();
      let comparison = [];
      if (this.editing && this.comparisonReady && (!this.projection || this.parentVersion)) {
        const base = this.comparisonBase;
        if (!this.comparisonCache || this.comparisonCache.base !== base || this.comparisonCache.candidate !== this.candidate) {
          this.comparisonCache = { base, candidate: this.candidate, blocks: previewChanges(base, this.candidate) };
        }
        comparison = this.comparisonCache.blocks;
        this.highlightState = advancePreviewHighlights(this.highlightState, comparison, {
          revision: this.preview.candidate_revision,
          candidate: this.candidate,
          enabled: !this.projection,
        });
      }
      const document = this.comparing ? null : previewMarkdown(this.candidate);
      const next = document ? document.paragraphs.map(text => ({ text, kind: 'equal',
        referenceLinks: document.referenceLinks, referenceKey: document.referenceKey })) : comparison;
      this.blocks = reconcilePreviewBlocks(this.blocks, next, () => `paragraph-${++this.nextBlock}`)
        .map(block => {
          let cached = this.contentCache.get(block.text);
          if (!cached || cached.referenceKey !== block.referenceKey) {
            cached = { referenceKey: block.referenceKey, html: this.renderMarkdown(block.text, block.referenceLinks) };
            this.contentCache.set(block.text, cached);
          }
          return { ...block, animate: Boolean(this.highlightState?.fresh.has(`${block.kind}\0${block.text}`)), html: cached.html };
        });
      if (this.editing) this.updateComparison(comparison);
      // Cache is transient and bounded by current document, never by its revision history.
      const current = new Set(this.blocks.map(block => block.text));
      for (const key of this.contentCache.keys()) if (!current.has(key)) this.contentCache.delete(key);
      this.$nextTick(() => {
        if (revision === this.readerNavigationRevision) this.restoreReader(reader);
      });
    },
    updateComparison(comparison) {
      const previous = this.comparisonSections;
      const previousByKey = new Map(previous.map(section => [section.key, section]));
      const oldIndex = this.activeChangeIndex;
      this.comparisonBlocks = reconcilePreviewBlocks(this.comparisonBlocks, comparison, () => `comparison-${++this.nextBlock}`);
      const sections = previewComparisonSections(this.comparisonBlocks, previous);
      if (this.editing && !this.projection && this.comparisonReady && this.candidate !== this.observedCandidate) {
        const changed = sections.filter(section => section.kind === 'change'
          ? section.signature !== previousByKey.get(section.key)?.signature
          : previousByKey.get(section.key)?.kind === 'change');
        if (changed.length) {
          this.pendingUpdates++;
          this.updateSerial++;
          this.lastChange = changed.at(-1).key;
        }
        this.observedCandidate = this.candidate;
      }
      this.comparisonSections = sections.map(section => ({ ...section,
        blocks: section.blocks.map(block => ({ ...block, html: this.contentCache.get(block.text)?.html || this.renderMarkdown(block.text, block.referenceLinks) })),
        fresh: !this.projection && section.blocks.some(block => this.highlightState?.fresh.has(`${block.kind}\0${block.text}`)),
        before: section.before?.map(block => ({ ...block, differenceHtml: this.artifactDifferencePassageHtml(block.segments, 'removed') })),
        after: section.after?.map(block => ({ ...block, differenceHtml: this.artifactDifferencePassageHtml(block.segments, 'added') })),
      }));
      if (!this.changeSections.some(section => section.key === this.activeChangeKey)) {
        this.activeChangeKey = this.changeSections[Math.min(oldIndex, this.changeSections.length - 1)]?.key || '';
      }
      if (!sections.some(section => section.key === this.lastChange)) this.lastChange = this.changeSections.at(-1)?.key || '';
    },
    async setView(view) {
      if (this.view === view) return;
      this.stopFollowing();
      this.view = view;
      await this.$nextTick();
      const target = this.$el.querySelector(view === 'changes' ? '[data-testid="artifact-diff-panel"]' : '[data-testid="document-preview-content"]');
      target?.focus({ preventScroll: true });
    },
    trackActiveChange() {
      if (!this.comparing || !this.scrollHost || !this.changeSections.length) return;
      const document = this.$refs.documentContent;
      if (!document) return;
      const header = this.$el.querySelector('.lexiusdoc-card__header--sticky');
      const top = Math.max(document.getBoundingClientRect().top, this.scrollHost.getBoundingClientRect().top, header?.getBoundingClientRect().bottom || 0) + 12;
      const targets = [...this.$el.querySelectorAll('[data-diff-index]')];
      const target = targets.find(element => element.getBoundingClientRect().bottom > top) || targets.at(-1);
      if (target) this.activeChangeKey = this.changeSections[Number(target.dataset.diffIndex)]?.key || '';
    },
    async goToChange(index) {
      const section = this.changeSections[Math.max(0, Math.min(index, this.changeSections.length - 1))];
      if (section) await this.revealChange(section.key);
    },
    navigateChange(direction) { return this.goToChange(this.activeChangeIndex + direction); },
    handleComparisonKey(event) {
      if (!this.comparing || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Escape'].includes(event.key)) return;
      event.preventDefault(); event.stopPropagation();
      if (event.key === 'Escape') this.setView('document');
      else this.navigateChange(event.key === 'ArrowLeft' ? -1 : 1);
    },
    async seeLastChange() {
      if (await this.revealChange(this.lastChange)) this.pendingUpdates = 0;
    },
    async revealChange(key) {
      this.stopFollowing();
      this.readerNavigationRevision++;
      this.revealingChange = true;
      try {
        if (this.view !== 'changes') this.view = 'changes';
        await this.$nextTick();
        const target = this.$el.querySelector(`[data-preview-section="${key}"]`);
        const host = this.$refs.documentContent;
        const chat = this.scrollHost;
        if (!target || !host || !chat) return false;
        this.activeChangeKey = key;
        this.programmatic = true;
        const headerHeight = this.$el.querySelector('.lexiusdoc-card__header--sticky')?.getBoundingClientRect().height || 0;
        const controls = this.$el.querySelector('.artifact-difference__navigator')?.getBoundingClientRect();
        const chatRect = chat.getBoundingClientRect();
        const visibleTop = Math.max(chatRect.top, 0) + headerHeight;
        const visibleBottom = Math.min(chatRect.bottom, window.innerHeight);
        // Reveal the controls when entering from the clean document or from
        // elsewhere in the chat. Subsequent navigation only scrolls the diff.
        if (controls && (controls.top < visibleTop || controls.bottom + 64 > visibleBottom)) {
          const next = chat.scrollTop + controls.top - visibleTop;
          if (this.setProgrammaticChatScrollTop) this.setProgrammaticChatScrollTop(chat, next);
          else chat.scrollTop = next;
        }
        host.scrollTop += target.getBoundingClientRect().top - host.getBoundingClientRect().top - 8;
        this.preserveDocumentPreviewPosition?.();
        target.focus({ preventScroll: true });
        requestAnimationFrame(() => { this.programmatic = false; });
        return true;
      } finally { this.revealingChange = false; }
    },
    denyCommittedVersion(version, projection = null) {
      this.access = 'revoked'; this.projection = null; this.savedComparisonBase = null; this.pendingComparisonVersion = null;
      this.selectedVersion = version; this.comparisonFeedback = '';
      this.deniedVersionEpochs.set(Number(version), this.fetchEpoch);
      this.versions = this.versions.map(item => Number(item.version_number) === Number(version)
        ? { version_number: item.version_number, parent_version_number: item.parent_version_number, available: false }
        : item);
      this.$emit('committed-unavailable', { preview: this.preview,
        committed: { artifact_id: this.committed.artifact_id, version }, ...(projection ? { projection } : {}) });
    },
    clearDeniedComparison() {
      if (!this.comparisonDenied) return;
      this.savedComparisonBase = null; this.comparisonCache = null; this.highlightState = null;
      this.comparisonFeedback = 'No pudimos abrir la versión anterior para comparar.';
    },
    denyComparisonVersion(version, epoch) {
      this.deniedVersionEpochs.set(version, epoch);
      this.versions = [...this.versions.filter(item => Number(item.version_number) !== version), { version_number: version, available: false }];
      this.clearDeniedComparison();
      if (this.projection && Number(this.selectedVersion) === version) this.denyCommittedVersion(version);
    },
    async loadVersions(epoch, signal) {
      try {
        const response = await fetch(`/chat/document-artifacts/${encodeURIComponent(this.committed.artifact_id)}/versions?chat_uuid=${encodeURIComponent(this.chatUuid)}`, { cache: 'no-store', signal });
        if (!response.ok) throw new Error('versions_unavailable');
        const payload = await response.json();
        if (!Array.isArray(payload.versions)) throw new Error('versions_unavailable');
        if (epoch !== this.fetchEpoch) return;
        this.versions = payload.versions.filter(item => item && Number(item.version_number) > 0).map(item => {
          const version = Number(item.version_number);
          if (item.available === false) this.deniedVersionEpochs.set(version, epoch);
          // An older history response cannot undo a denial from this load attempt.
          return this.deniedVersionEpochs.get(version) === epoch ? { ...item, available: false } : item;
        });
        if (!this.projection && this.pendingCommittedVersion) {
          const pending = this.versions.find(item => Number(item.version_number) === Number(this.pendingCommittedVersion));
          const parent = Number(pending?.parent_version_number);
          if (Number.isInteger(parent) && parent > 0) this.pendingComparisonVersion = parent;
        }
        this.clearDeniedComparison();
        const deniedSelection = [this.pendingCommittedVersion, this.projection && this.selectedVersion]
          .find(version => version && this.deniedVersionEpochs.get(Number(version)) === epoch);
        if (deniedSelection) this.denyCommittedVersion(deniedSelection);
        this.versionsFeedback = '';
      } catch (error) {
        if (epoch !== this.fetchEpoch || error.name === 'AbortError') return;
        // History is optional: a failure must not restart a successful document load.
        this.versionsFeedback = 'No pudimos cargar el historial de versiones.';
      }
    },
    async loadComparisonBase(epoch, signal, projection = this.projection) {
      const parentVersion = Number(projection?.parent_version);
      if (!this.editing || !Number.isInteger(parentVersion) || parentVersion <= 0) return { content: null, feedback: '' };
      const query = new URLSearchParams({ chat_uuid: this.chatUuid, version: String(parentVersion) });
      try {
        const response = await fetch(`/chat/document-artifacts/${encodeURIComponent(this.committed.artifact_id)}?${query}`, { cache: 'no-store', signal });
        if (epoch !== this.fetchEpoch) return;
        if (response.status === 403) {
          this.denyComparisonVersion(parentVersion, epoch);
        }
        if (!response.ok) throw new Error('parent_unavailable');
        const parent = await response.json();
        if (epoch !== this.fetchEpoch) return;
        if (parent.available === false) {
          this.denyComparisonVersion(parentVersion, epoch);
        }
        if (parent.available === false || Number(parent.selected_version) !== parentVersion
          || (parent.artifact_id && String(parent.artifact_id) !== String(this.committed.artifact_id))
          || typeof parent.content_markdown !== 'string') throw new Error('parent_unavailable');
        // A history denial may arrive while this request is in flight.
        if (this.deniedVersionEpochs.get(parentVersion) === epoch) throw new Error('parent_unavailable');
        this.versions = this.versions.map(item => Number(item.version_number) === parentVersion ? { ...item, available: true } : item);
        return { content: String(parent.content_rendered_markdown || parent.content_markdown), feedback: '' };
      } catch (error) {
        if (epoch !== this.fetchEpoch || error.name === 'AbortError') return;
        return { content: null, feedback: 'No pudimos abrir la versión anterior para comparar.' };
      }
    },
    retryCommitted() { return this.loadCommitted(this.retryVersion); },
    async loadCommitted(version = null, { retry = false } = {}) {
      if (!this.committed?.artifact_id) return;
      clearTimeout(this.retryTimer); this.retryTimer = null;
      if (!retry) { this.retries = 0; this.feedback = ''; }
      this.controller?.abort(); this.controller = new AbortController(); const epoch = ++this.fetchEpoch;
      this.latchedCommitted = { ...this.committed };
      const exactVersion = version || this.committed.version;
      this.retryVersion = exactVersion;
      this.pendingCommittedVersion = exactVersion;
      this.pendingComparisonVersion = null;
      const versionsRequest = this.loadVersions(epoch, this.controller.signal);
      const query = new URLSearchParams({ chat_uuid: this.chatUuid, version: String(exactVersion) });
      try {
        const response = await fetch(`/chat/document-artifacts/${encodeURIComponent(this.committed.artifact_id)}?${query}`, { cache: 'no-store', signal: this.controller.signal });
        if (epoch !== this.fetchEpoch) return;
        if (response.status === 403) {
          this.denyCommittedVersion(exactVersion);
          return;
        }
        if (response.status === 401) this.access = 'paused';
        if (!response.ok) throw new Error('unavailable');
        const projection = await response.json();
        if (epoch !== this.fetchEpoch) return;
        if (projection.available === false) {
          this.denyCommittedVersion(exactVersion, projection);
          return;
        }
        if (Number(projection.selected_version) !== Number(exactVersion)
          || (projection.artifact_id && String(projection.artifact_id) !== String(this.committed.artifact_id))) throw new Error('version_mismatch');
        if (this.deniedVersionEpochs.get(Number(exactVersion)) === epoch) {
          this.denyCommittedVersion(exactVersion);
          return;
        }
        this.pendingComparisonVersion = Number(projection.parent_version) || null;
        this.clearDeniedComparison();
        // Keep the current reading surface until both authorized sides are ready.
        const comparison = await this.loadComparisonBase(epoch, this.controller.signal, projection);
        if (epoch !== this.fetchEpoch) return;
        if (this.deniedVersionEpochs.get(Number(exactVersion)) === epoch) {
          this.denyCommittedVersion(exactVersion);
          return;
        }
        this.versions = this.versions.map(item => Number(item.version_number) === Number(exactVersion) ? { ...item, available: true } : item);
        this.savedComparisonBase = comparison?.content ?? null; this.comparisonFeedback = comparison?.feedback || '';
        this.projection = projection; this.selectedVersion = exactVersion; this.access = '';
        this.pendingComparisonVersion = null;
        this.clearDeniedComparison();
        this.$emit('committed-ready', { preview: this.preview, committed: this.committed, projection });
        if (epoch === this.fetchEpoch) this.retries = 0;
      } catch (error) {
        if (epoch !== this.fetchEpoch || error.name === 'AbortError') return;
        if (this.retries++ < 5) this.retryTimer = setTimeout(() => {
          if (epoch === this.fetchEpoch) return this.loadCommitted(version, { retry: true });
        }, Math.min(500 * 2 ** this.retries, 8000));
        else this.feedback = 'No pudimos abrir la versión guardada. Reintentar';
      } finally {
        await versionsRequest;
        if (epoch === this.fetchEpoch) this.pendingCommittedVersion = null;
      }
    },
    async copyIncomplete() {
      try { await navigator.clipboard.writeText(readablePreviewText(this.candidate)); this.feedback = 'Borrador incompleto copiado'; }
      catch { this.feedback = 'No se pudo copiar'; }
    },
    exportDocument(action) {
      if (!this.ready || this.actionPending) return;
      this.actionPending = true;
      const matchesDocument = this.receipt?.result_ref?.type === 'document'
        && String(this.receipt.result_ref.id || '') === String(this.committed.artifact_id);
      const receipt = matchesDocument ? this.receipt : { tool_name: 'crear_editar_archivo', tool_run_id: this.preview.tool_run_id,
        result_ref: { type: 'document', id: this.committed.artifact_id }, ui_payload: { counts: { version: this.selectedVersion } } };
      this.$emit('action', { action, receipt, artifactVersion: this.selectedVersion,
        artifactClipboardContent: this.cleanExport,
        complete: success => { this.actionPending = false; this.feedback = success ? 'Listo' : 'No se pudo completar'; } });
    },
  },
  template: `
    <section class="lexiusdoc-card dynamic-color tool-receipt__artifact-document document-preview-card" data-testid="document-preview-card" :data-preview-id="preview.preview_id" @focusin="stopFollowing" @keydown="stopFollowing">
      <header class="lexiusdoc-card__header" :class="{ 'lexiusdoc-card__header--sticky': !committed }">
        <div class="lexiusdoc-card__info">
          <div class="lexiusdoc-card__icon"><i class="anticon anticon-file-text" aria-hidden="true"></i></div>
          <div class="lexiusdoc-card__meta">
            <span class="lexiusdoc-card__label">Documento generado</span>
            <h4 class="lexiusdoc-card__title">{{ title }}</h4>
            <p v-if="savedMetadata" class="lexiusdoc-card__hint">{{ savedMetadata }}</p>
            <p class="lexiusdoc-card__hint" role="status" aria-live="polite" data-testid="document-preview-status">{{ status }}<span v-if="status === 'Redactando'" class="document-preview-spinner" data-testid="document-preview-spinner" aria-hidden="true"></span><span v-if="preview.section && !ready && !disclosureRevoked"> · {{ preview.section }}</span></p>
          </div>
        </div>
        <div class="lexiusdoc-card__actions">
          <button type="button" class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--copy" data-testid="document-preview-copy" aria-label="Copiar documento" title="Copiar documento" :disabled="!ready || actionPending" @click="exportDocument('Copiar')"><i class="anticon anticon-copy" aria-hidden="true"></i></button>
          <button type="button" class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--pdf" data-testid="document-preview-pdf" aria-label="Descargar PDF" title="Descargar PDF" :disabled="!ready || actionPending" @click="exportDocument('artifact-export-pdf')"><i class="anticon anticon-file-pdf" aria-hidden="true"></i></button>
          <button type="button" class="lexiusdoc-card__action-btn lexiusdoc-card__action-btn--docx" data-testid="document-preview-docx" aria-label="Descargar Word" title="Descargar Word" :disabled="!ready || actionPending" @click="exportDocument('artifact-export-docx')"><i class="anticon anticon-file-word" aria-hidden="true"></i></button>
        </div>
      </header>
      <p v-if="changeSummary" class="document-preview-summary" data-testid="document-preview-change-summary">{{ changeSummary }}</p>
      <ul v-if="warnings.length" class="tool-receipt__warnings" data-testid="document-preview-warnings">
        <li v-for="(warning, index) in warnings" :key="index">{{ warning }}</li>
      </ul>
      <div v-if="showComparisonViews" class="artifact-difference__entry" :class="{ 'is-open': comparing }">
        <span class="artifact-difference__entry-icon" aria-hidden="true"><i class="anticon anticon-diff"></i></span>
        <div class="artifact-difference__entry-copy">
          <div class="artifact-difference__entry-heading">
            <strong>Revisión de cambios</strong>
            <button type="button" class="artifact-difference__help" :aria-expanded="helpOpen" :aria-controls="comparisonId + '-help'" aria-label="Ayuda para editar y comparar versiones del documento" @click="helpOpen = !helpOpen"><i class="anticon anticon-question" aria-hidden="true"></i></button>
          </div>
          <span>{{ comparisonDescription }}</span>
        </div>
        <button type="button" class="artifact-difference__entry-button" data-testid="artifact-diff-open" :aria-expanded="comparing" :aria-controls="comparisonId" @click="setView(comparing ? 'document' : 'changes')">
          <i :class="['anticon', comparing ? 'anticon-arrow-left' : 'anticon-diff']" aria-hidden="true"></i>
          <span>{{ comparing ? 'Volver al documento' : 'Ver diferencias' }}</span>
        </button>
        <p v-if="helpOpen" :id="comparisonId + '-help'" class="document-preview-help">Pide el cambio en este mismo chat e indica qué quieres modificar y qué debe mantenerse. Los cambios aparecen aquí a medida que llegan. Al terminar, LEXIUS guarda una nueva versión y conserva las anteriores.</p>
      </div>
      <nav v-if="versions.length > 1" class="tool-workspace__versions tool-receipt__artifact-versions" aria-label="Historial de versiones">
        <button v-for="version in versions" :key="version.version_number" type="button" :disabled="version.available === false" :aria-label="version.available === false ? 'Versión ' + version.version_number + ' no disponible' : 'Abrir versión ' + version.version_number" :title="version.change_summary || 'Versión ' + version.version_number" :aria-pressed="Number(selectedVersion) === Number(version.version_number)" :aria-current="Number(selectedVersion) === Number(version.version_number) ? 'true' : null" :aria-busy="Number(pendingCommittedVersion) === Number(version.version_number)" @click="loadCommitted(version.version_number)">v{{ version.version_number }}</button>
      </nav>
      <p v-if="versionsFeedback" role="status">{{ versionsFeedback }} <button type="button" @click="loadVersions(fetchEpoch, controller.signal)">Reintentar historial</button></p>
      <section v-show="hasDocument" :id="comparisonId" :class="comparing ? 'artifact-difference' : 'lexiusdoc-card__body-frame'" :data-testid="comparing ? 'artifact-diff-panel' : null" :aria-label="comparing ? 'Control de cambios' : 'Documento'" tabindex="-1" @keydown="handleComparisonKey">
        <header v-if="comparing" class="artifact-difference__header">
          <div>
            <span class="artifact-difference__eyebrow">Control de cambios</span>
            <h5>{{ comparisonTitle }}</h5>
            <p>Lee el documento completo en su orden original. Cada edición muestra el texto anterior junto al nuevo en el lugar donde se hizo.</p>
          </div>
          <button type="button" class="artifact-difference__close" @click="setView('document')"><i class="anticon anticon-arrow-left" aria-hidden="true"></i><span>Volver al documento</span></button>
        </header>
        <div v-if="comparing && !comparisonReady" class="artifact-difference__state" :class="{ 'artifact-difference__state--error': comparisonFeedback }" role="status">
          <span :class="comparisonFeedback ? 'artifact-difference__state-icon' : 'artifact-difference__loader'" aria-hidden="true"><i :class="comparisonFeedback ? 'anticon anticon-warning' : 'anticon anticon-loading anticon-spin'"></i></span>
          <div><strong>{{ comparisonFeedback ? 'No se pudo abrir la comparación' : 'Comparando ambas versiones' }}</strong><span>{{ comparisonFeedback || 'Cargando la versión anterior para comparar' }}</span></div>
          <button v-if="comparisonFeedback" type="button" @click="retryCommitted">Reintentar comparación</button>
        </div>
        <template v-else-if="comparing">
          <div class="artifact-difference__summary" data-testid="artifact-diff-summary">
            <div class="artifact-difference__legend" aria-label="Leyenda de cambios">
              <span class="is-added"><i aria-hidden="true"></i>Texto añadido</span>
              <span class="is-removed"><i aria-hidden="true"></i>Texto eliminado</span>
            </div>
            <dl aria-label="Resumen de cambios">
              <div><dt>Cambios</dt><dd>{{ changeSections.length }}</dd></div>
              <div class="is-added"><dt>Adiciones</dt><dd>+{{ changeCounts.additions }}</dd></div>
              <div class="is-removed"><dt>Eliminaciones</dt><dd>−{{ changeCounts.removals }}</dd></div>
            </dl>
          </div>
          <p v-if="comparisonTruncated" class="artifact-difference__warning">La revisión contiene cambios muy amplios; algunos pasajes se muestran como bloques completos.</p>
          <div v-if="changeSections.length" class="artifact-difference__navigator">
            <p data-testid="artifact-diff-position" aria-live="polite" aria-atomic="true">Cambio <strong>{{ activeChangeIndex + 1 }}</strong> de {{ changeSections.length }}</p>
            <div class="artifact-difference__navigator-controls">
              <button type="button" class="artifact-difference__navigator-button" data-testid="artifact-diff-first" :disabled="activeChangeIndex === 0" @click="goToChange(0)"><i class="anticon anticon-double-left" aria-hidden="true"></i><span>Primero</span></button>
              <button type="button" class="artifact-difference__navigator-button" data-testid="artifact-diff-prev" :disabled="activeChangeIndex === 0" @click="navigateChange(-1)"><i class="anticon anticon-arrow-left" aria-hidden="true"></i><span>Anterior</span></button>
              <button type="button" class="artifact-difference__navigator-button artifact-difference__navigator-button--next" data-testid="artifact-diff-next" :disabled="activeChangeIndex >= changeSections.length - 1" @click="navigateChange(1)"><span>Siguiente</span><i class="anticon anticon-arrow-right" aria-hidden="true"></i></button>
              <button type="button" class="artifact-difference__navigator-button" data-testid="artifact-diff-last" :disabled="activeChangeIndex >= changeSections.length - 1" @click="goToChange(changeSections.length - 1)"><span>Último</span><i class="anticon anticon-double-right" aria-hidden="true"></i></button>
            </div>
            <span class="artifact-difference__keyboard-hint">También puedes usar ← y →</span>
          </div>
          <div v-else class="artifact-difference__empty" data-testid="artifact-diff-empty" role="status">
            <i class="anticon anticon-check-circle" aria-hidden="true"></i>
            <strong>{{ ready ? 'No hay cambios de texto' : 'Aún no hay cambios de texto' }}</strong>
            <span>{{ ready ? 'El contenido de ambas versiones coincide.' : 'Los cambios aparecerán aquí a medida que se completen.' }}</span>
          </div>
        </template>
        <article ref="documentContent" :class="comparing ? 'artifact-difference__document' : ['lexiusdoc-card__content', { 'document-preview-document--editable': editing }]" data-testid="document-preview-content" tabindex="-1" @scroll.passive="onDocumentScroll">
          <div v-for="section in displaySections" :key="section.key" :data-preview-section="section.key" :data-diff-index="section.kind === 'change' ? section.index : null" :data-testid="section.kind === 'change' ? 'artifact-diff-change' : 'artifact-diff-context'" :class="section.kind === 'change' ? ['artifact-difference__change', { 'is-active': activeChangeKey === section.key, 'document-preview-block--fresh': section.fresh }] : (comparing ? 'artifact-difference__context lexiusdoc-card__content' : ['document-preview-passage', { 'is-changed': section.change, 'is-deleted': section.kind === 'deletion', 'document-preview-passage--fresh': section.fresh }])" :aria-current="section.kind === 'change' && activeChangeKey === section.key ? 'step' : null" :aria-label="section.kind === 'change' ? 'Cambio ' + (section.index + 1) + ' de ' + changeSections.length + ': ' + section.label : null" :tabindex="comparing ? -1 : null" @click="section.kind === 'change' && (activeChangeKey = section.key)">
            <template v-if="section.kind === 'change'">
              <div class="artifact-difference__rail" aria-hidden="true"><span>{{ section.index + 1 }}</span></div>
              <div class="artifact-difference__change-body">
                <span class="artifact-difference__kind">Cambio {{ section.index + 1 }} · {{ section.label }}</span>
                <div class="artifact-difference__pair">
                  <section class="artifact-difference__version artifact-difference__version--before">
                    <header><span>Antes</span><small>{{ parentVersion ? 'v' + parentVersion : 'Original' }}</small></header>
                    <div>
                      <div v-for="block in section.before" :key="block.key" :data-preview-block="block.key" :data-message-viewport-ref="'preview:' + preview.preview_id + ':' + block.key" class="document-preview-block document-preview-block--removed"><div data-preview-block-content v-html="block.differenceHtml"></div></div>
                      <p v-if="!section.before.length" class="artifact-difference__passage artifact-difference__passage--empty">Este pasaje no existía.</p>
                    </div>
                  </section>
                  <section class="artifact-difference__version artifact-difference__version--after">
                    <header><span>Ahora</span><small>{{ selectedVersion ? 'v' + selectedVersion : 'En edición' }}</small></header>
                    <div>
                      <div v-for="block in section.after" :key="block.key" :data-preview-block="block.key" :data-message-viewport-ref="'preview:' + preview.preview_id + ':' + block.key" class="document-preview-block document-preview-block--added"><div data-preview-block-content v-html="block.differenceHtml"></div></div>
                      <p v-if="!section.after.length" class="artifact-difference__passage artifact-difference__passage--empty">Este pasaje fue eliminado.</p>
                    </div>
                  </section>
                </div>
              </div>
            </template>
            <template v-else>
              <span v-if="section.fresh" :key="updateSerial" class="document-preview-passage-highlight" aria-hidden="true"></span>
              <button v-if="section.marker" type="button" class="document-preview-change-marker" data-testid="document-preview-change-marker" :aria-label="'Ver cambio ' + (section.change.index + 1) + ': ' + section.change.label" :title="'Ver cambio ' + (section.change.index + 1) + ': ' + section.change.label" @click="revealChange(section.change.key)"><span aria-hidden="true">{{ section.kind === 'deletion' ? '−' : section.change.index + 1 }}</span></button>
              <span v-if="section.kind === 'deletion'" class="document-preview-deletion-label" aria-hidden="true">Texto eliminado</span>
              <div v-for="block in section.blocks" :key="block.key" :data-preview-block="block.key" :data-message-viewport-ref="'preview:' + preview.preview_id + ':' + block.key" class="document-preview-block document-preview-block--equal"><div data-preview-block-content v-html="block.html"></div></div>
            </template>
          </div>
        </article>
      </section>
      <p v-if="feedback || (!ready && committed)" role="status">{{ feedback }} <button v-if="!ready && committed" type="button" @click="retryCommitted">Reintentar</button></p>
      <button v-if="!committed && (preview.disclosure_paused || preview.unavailable) && retryDocumentPreview" type="button" @click="retryDocumentPreview(preview.preview_id)">Reintentar vista previa</button>
      <button v-if="incomplete && hasDocument" type="button" @click="copyIncomplete">Copiar borrador incompleto</button>
      <div v-if="showUpdateNotice" class="document-preview-updates" :class="{ 'is-complete': editComplete, 'has-updates': pendingUpdates > 0, 'is-incomplete': incomplete }" data-testid="document-preview-updates">
        <div role="status" aria-live="polite" aria-atomic="true">
          <i v-if="editComplete" class="anticon anticon-check-circle" aria-hidden="true"></i>
          <i v-else-if="incomplete" class="anticon anticon-warning" aria-hidden="true"></i>
          <span v-else :key="updateSerial" class="document-preview-update-dot" :class="{ 'is-new': pendingUpdates > 0 }" aria-hidden="true"></span>
          <span><strong>{{ updateAnnouncement }}</strong><small v-if="editComplete">{{ versionRetained ? 'Se conserva la versión actual' : 'Documento guardado' }}<span v-if="pendingUpdates && !versionRetained"> · {{ pendingUpdates }} {{ pendingUpdates === 1 ? 'actualización recibida' : 'actualizaciones recibidas' }}</span></small><small v-else-if="pendingUpdates && !incomplete">{{ status }}</small></span>
        </div>
        <button v-if="!versionRetained && lastChange && comparisonReady" type="button" class="artifact-difference__entry-button" data-testid="document-preview-latest" @click="seeLastChange"><i class="anticon anticon-diff" aria-hidden="true"></i><span>Ver último cambio</span></button>
      </div>
    </section>
  `,
};
